import Tooltip, { TooltipDesc, TooltipName } from "components/base/Tooltip";
import { media } from "components/styles/breakpoints";
import { getAdjacentSectors } from "libs/character";
import { getAreaData, getPlanetData } from "libs/stats";
import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { showLinkSectorsOverlay } from "redux/actions";
import {
  getLocationInfo,
  getTravelInfo,
} from "redux/selectors/characterSelectors";
import { getFightStatus } from "redux/selectors/fightSelectors";
import styled, { css } from "styled-components";

const MiniMapWrapper = styled.div`
  position: absolute;
  top: 3dvh;
  right: 3dvh;
  z-index: 6;
  background-color: rgba(0, 0, 0, 0.4);
  backdrop-filter: blur(20px);
  border-radius: 4px;

  ${media.desktop`
    top: 5dvh;
    right: 5dvh;
  `}
`;

const InfoWrapper = styled.div`
  display: flex;
  align-items: center;
  padding: clamp(8px, 1.5dvh, 12px) clamp(10px, 1dvw, 15px);
  gap: clamp(10px, 1dvw, 15px);
`;

const PlanetName = styled.div<{ $color: string }>`
  font-size: 16px;
  text-transform: uppercase;
  text-align: center;
  font-weight: 600;
  color: ${(props) => props.$color};
`;

const SectorsList = styled.div`
  display: flex;
  align-items: center;
  margin: 0 40px;
  margin-top: 18px;
`;

const currentSectorMixin = css`
  opacity: 1;
`;

const Sector = styled.div<{
  $isCurrentSector: boolean;
}>`
  display: flex;
  flex-direction: column;
  position: relative;
  align-items: center;
  opacity: 0.3;

  ${({ $isCurrentSector }) => $isCurrentSector && currentSectorMixin}
`;

const currentSectorIconMixin = css`
  &::after {
    content: "";
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 8px;
    height: 8px;
    background: white;
    border-radius: 50%;
  }
`;

const SectorName = styled.div`
  font-size: 12px;
  text-transform: uppercase;
  position: absolute;
  bottom: 100%;
  text-align: center;
  white-space: nowrap;
  margin-bottom: 3px;
  font-weight: 600;
  color: white;
`;

const SectorIcon = styled.div<{ $isCurrentSector: boolean }>`
  width: 12px;
  height: 12px;
  border: 2px solid white;
  border-radius: 50%;

  ${({ $isCurrentSector }) => $isCurrentSector && currentSectorIconMixin}
`;

const SectorLine = styled.div`
  width: 80px;
  border-bottom: 1px solid white;
  height: 0;
  opacity: 0.3;
`;

export default function MiniMap() {
  const dispatch = useDispatch();

  const { planet, area: currentSector } = useSelector(getLocationInfo);
  const { isTravelMode } = useSelector(getTravelInfo);
  const fightStatus = useSelector(getFightStatus);

  const { name: planetName, color: planetColor } = getPlanetData(planet);
  const { name: currentSectorName } = getAreaData(currentSector);
  const { left: leftSector, right: rightSector } =
    getAdjacentSectors(currentSector);
  const sectors = [leftSector, currentSector, rightSector].filter(
    (sector) => !!sector
  ) as string[];

  const isHidden = fightStatus === "fighting" || isTravelMode;
  if (isHidden) {
    return null;
  }

  return (
    <MiniMapWrapper
      onClick={() => {
        dispatch(showLinkSectorsOverlay());
      }}
    >
      <Tooltip
        content={
          <>
            <TooltipName>Current Location</TooltipName>
            <TooltipDesc>
              You are currently in sector {currentSectorName} on planet{" "}
              {planetName}.
            </TooltipDesc>
            <TooltipDesc>
              Click on this map to instantly travel to linked sectors within a
              planet.
            </TooltipDesc>
            <TooltipDesc>
              You can also travel to different planets by visiting the nearest
              Jump Gate.
            </TooltipDesc>
          </>
        }
        color={planetColor}
      >
        <InfoWrapper>
          <PlanetName $color={planetColor}>{planetName}</PlanetName>
          <SectorsList>
            {sectors.map((sector, index) => {
              const isCurrentSector = currentSector === sector;
              const sectorData = getAreaData(sector);

              return (
                <React.Fragment key={sector}>
                  <Sector $isCurrentSector={isCurrentSector}>
                    <SectorIcon $isCurrentSector={isCurrentSector} />
                    <SectorName>{sectorData.name}</SectorName>
                  </Sector>
                  {index < sectors.length - 1 && <SectorLine />}
                </React.Fragment>
              );
            })}
          </SectorsList>
        </InfoWrapper>
      </Tooltip>
    </MiniMapWrapper>
  );
}
